import type { ExperimentEvaluationOptions, StatsigOptions, StatsigUser, TypedGet } from '@statsig/js-client';
import { LogLevel, StatsigClient } from '@statsig/js-client';
import Config from '@util/config';
import { VALID_STATSIG_ENVS } from '@util/constants';
import { logger } from '@util/observability';

const determineEnvTier = () => {
    switch (Config.ENV) {
        case 'production':
            return VALID_STATSIG_ENVS.production;
        case 'staging':
            return VALID_STATSIG_ENVS.staging;
        default:
            return VALID_STATSIG_ENVS.development;
    }
};

const options: StatsigOptions = {
    environment: {
        tier: determineEnvTier(),
    },
    networkConfig: {
        preventAllNetworkTraffic: process.env.NODE_ENV === 'test',
    },
    logLevel: process.env.NODE_ENV === 'test' ? LogLevel.None : undefined,
};

interface State {
    client: StatsigClient;
    initialized: boolean;
    updateUserQueue: Promise<boolean>;
}

// Helper function to get the stable ID from localStorage
const getStableIDFromStorage = (): string | null => {
    try {
        return localStorage.getItem('STATSIG_LOCAL_STORAGE_STABLE_ID');
    } catch (error) {
        // Handle cases where localStorage is not available (SSR, etc.)
        return null;
    }
};

// Create user object with stable ID if available
const createInitialUser = () => {
    const baseUser = { appVersion: 'tm-web' };
    const stableID = getStableIDFromStorage();

    if (stableID) {
        return {
            ...baseUser,
            customIDs: { stableID },
        };
    }

    return baseUser;
};

const state: State = {
    client: new StatsigClient(Config.STATSIG_CLIENT_API_KEY, createInitialUser(), options),
    initialized: false,
    updateUserQueue: Promise.resolve(true),
};

export const initialize = async () => {
    const init = await state.client.initializeAsync();
    state.initialized = init.success;
    if (!init.success) {
        logger.error('Failed to initialize Statsig', { error: init.error });
    }
    return init.success;
};

export const featureGateValue = (key: string, defaultValue: boolean) => {
    if (!state.initialized) return defaultValue;
    return state.client.checkGate(key);
};

export const updateUser = async (user: Pick<StatsigUser, 'userID' | 'email'> & { orgId?: string; teamId?: string }) => {
    if (!state.initialized) return Promise.reject(false);

    // Queue the update to prevent race conditions
    state.updateUserQueue = state.updateUserQueue.then(async () => {
        const { orgId, teamId, ...userData } = user;
        // Preserve the stable ID when updating user
        const stableID = getStableIDFromStorage();
        const customIDs = {
            ...(stableID && { stableID }),
            ...(orgId && { org_id: orgId }),
            ...(teamId && { team_id: teamId }),
        };
        const withAppVersion = {
            ...userData,
            appVersion: 'tm-web',
            ...(Object.keys(customIDs).length > 0 && { customIDs }),
        };

        let details = await state.client.updateUserAsync(withAppVersion);
        if (!details.success) {
            logger.error('Failed to update Statsig user', { error: details.error });
            // try to get cached value on failure
            details = state.client.updateUserSync(withAppVersion);
        }
        return details.success;
    });

    return state.updateUserQueue;
};

interface ExperimentEventArgs {
    customEventName: string;
    customValue?: string | number | undefined;
    metadata?: Record<string, string> | undefined;
}

export const logCustomEvent = ({ customEventName, customValue, metadata }: ExperimentEventArgs): void => {
    if (!state.initialized) return;
    return state.client.logEvent(customEventName, customValue, metadata);
};

/** triggers an exposure event for the experiment value you are trying to get, unless you provide options.disableExposureLog */
export function getExperiment(
    experimentName: string,
    defaultValues: { [p: string]: any },
    options: ExperimentEvaluationOptions = { disableExposureLog: false },
) {
    const getValueOrDefault = (callback: TypedGet) => {
        return Object.keys(defaultValues).reduce(
            (acc, key) => {
                acc[key] = callback(key, defaultValues[key]);
                return acc;
            },
            {} as { [p: string]: any },
        );
    };

    if (!state.initialized) return null;

    const experiment = state.client.getExperiment(experimentName, options);

    switch (experiment.details.reason) {
        case 'Unrecognized':
            throw new Error(`${experimentName} was Unrecognized`);
        case 'NoValues':
            throw new Error(`${experimentName} returned No Values`);
        case 'Uninitialized':
            throw new Error(`statsig client was uninitialized while trying to get ${experimentName}`);
    }

    return {
        getValue: () => getValueOrDefault(experiment.get),
        group: experiment.groupName ?? 'Control',
        isActive: !!experiment.__evaluation?.is_experiment_active,
    };
}
